import { useRef } from 'react';
import { C, soft } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { FONT_FAMILIES, fontFamilyVar } from '@parsina/framework/clinical/src/constants/fonts';

// ─────────────────────────────────────────────────────────────────────────────
// Typeface picker.
//
// Shared by both surfaces for the same reason ThemePicker is: a grid of
// samples behaves identically on a phone and a workstation. Each option is set
// in ITS OWN face (through the `--font-<id>` variable styles.css declares), so
// the doctor compares the actual glyphs rather than a name — a name means
// nothing to someone choosing between Sahel and Shabnam.
//
// Same accessibility contract as ThemePicker: a real radiogroup, arrow keys
// move and select, only the selected option is tabbable, and the selected
// state is carried by a ring and a checkmark, never by colour alone.
// ─────────────────────────────────────────────────────────────────────────────

export default function FontFamilyPicker({ value, onChange, testId = 'settings-font' }) {
  const { t, isRTL } = useTranslation();
  const refs = useRef({});
  const options = FONT_FAMILIES;

  const onKeyDown = (e) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();

    const i = options.findIndex((f) => f.id === value);
    const current = i === -1 ? 0 : i;
    const forward = isRTL
      ? (e.key === 'ArrowLeft' || e.key === 'ArrowDown')
      : (e.key === 'ArrowRight' || e.key === 'ArrowDown');
    const step = forward ? 1 : -1;

    let next;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = options.length - 1;
    else next = (current + step + options.length) % options.length;

    const id = options[next].id;
    onChange(id);
    refs.current[id]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('settings.appearance.fontFamilyTitle')}
      data-testid={testId}
      onKeyDown={onKeyDown}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(210px, 100%), 1fr))',
        gap: 12,
      }}
    >
      {options.map((font) => {
        const selected = font.id === value;
        return (
          <button
            key={font.id}
            ref={(el) => { refs.current[font.id] = el; }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            data-testid={`${testId}-option-${font.id}`}
            onClick={() => onChange(font.id)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6,
              minHeight: 44, padding: '12px 14px',
              borderRadius: 10, cursor: 'pointer', textAlign: 'start',
              background: selected ? soft(C.navy, 0.06) : C.card,
              border: `1.5px solid ${selected ? C.navy : C.border}`,
              boxShadow: selected ? `0 0 0 3px ${soft(C.navy, 0.13)}` : 'none',
              transition: 'border-color .15s, box-shadow .15s, background .15s',
              // The option is set in the face it offers — that IS the preview.
              fontFamily: fontFamilyVar(font.id),
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                flex: 1, minWidth: 0, fontSize: '0.875rem',
                fontWeight: selected ? 700 : 600,
                color: selected ? C.textPrimary : C.textSecondary,
              }}>
                {t(font.labelKey)}
              </span>
              {selected && (
                <svg viewBox="0 0 24 24" width={16} height={16} fill={C.navy} aria-hidden style={{ flexShrink: 0 }}>
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              )}
            </span>
            {/* A line of the kind of text the panel is full of — a Persian
                sentence with Latin and digits inside it — so the choice is
                judged on the mix the doctor will actually read. */}
            <span style={{ fontSize: '0.8125rem', lineHeight: 1.7, color: C.textPrimary }}>
              {t('settings.appearance.fontSample')}
            </span>
            <span style={{ fontSize: '0.6875rem', color: C.textMuted, lineHeight: 1.6 }}>
              {t(font.hintKey)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
